import { useEffect, useState } from "react";
import {
  Card, Stack, Badge, Button, TextInput, Group, Drawer, Divider, Text,
  Table, Box, ActionIcon, Tooltip, NumberInput, Select,
} from "@mantine/core";
import {
  IconCheck, IconPlus, IconSearch, IconEye, IconPencil, IconX, IconShoppingCart,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useInfiniteResource, useAction, useResource } from "../../../shared/api";
import { PurchaseOrderDetailDrawer } from "./PurchaseOrders";
import { SupplierInvoiceDetailModal } from "../components/SupplierInvoiceDetailModal";
import {
  PageHeader, EmptyState, InfiniteTable, formatQty, formatTRY, formatDateTime,
} from "../../../shared/ui";
import { GoodsReceiptFormModal } from "../components/GoodsReceiptFormModal";
import {
  SupplierInvoiceFormModal, type InvoicePrefillLine,
} from "../components/SupplierInvoiceFormModal";
import {
  purchaseUnitsFor, factorFor, lineToPayload,
} from "../components/goodsReceiptUnits";
import { IconFileInvoice } from "@tabler/icons-react";

// Kural: SON adım (Faturalandı) = yeşil, bir önceki (Onaylandı) = mavi.
const STATUS_COLOR: Record<string, string> = {
  // DRAFT = mal girişi oluşturuldu ama henüz onaylanmadı → "Onay Bekliyor"
  // (turuncu, aksiyon gerektirdiğini vurgular). Onayla → CONFIRMED (stok girer).
  DRAFT: "orange", CONFIRMED: "blue", CANCELLED: "red",
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Onay Bekliyor", CONFIRMED: "Onaylandı", CANCELLED: "İptal",
};

export function GoodsReceiptsPage() {
  // Stok Operasyonları'ndan "Mal Girişi GR-..." referansı tıklandığında
  // sessionStorage üzerinden gelen highlight id'si. Satırı vurgular, arama
  // kutusunu doldurur ve gelen kayıt yüklendiğinde detay drawer'ını açar.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  // Görünen durum filtresi. "Faturalandı" gerçek bir statü değil
  // (CONFIRMED + faturalı) — backend `status` + `invoiced` paramına çevrilir.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const statusParams: Record<string, string> =
    statusFilter === "DRAFT" ? { status: "DRAFT" }
    : statusFilter === "CONFIRMED" ? { status: "CONFIRMED", invoiced: "no" }
    : statusFilter === "INVOICED" ? { status: "CONFIRMED", invoiced: "yes" }
    : statusFilter === "CANCELLED" ? { status: "CANCELLED" }
    : {};
  // Bağlı PO'yu yan drawer'da göster (sayfaya gitmeden). {id} yeter — drawer taze çeker.
  const [poDetail, setPoDetail] = useState<{ id: string } | null>(null);
  // Bağlı faturayı detay modalında göster (ödeme durumu + ek indir/yükle).
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  // Referans tıklamasından sonra ilgili kayıt listeye düştüğünde drawer'ı
  // bir kez otomatik açmak için — kullanıcı kapatınca tekrar açılmasın.
  const [autoOpened, setAutoOpened] = useState(false);
  // Detay drawer'ında gösterilen GR (satır tıklamasıyla veya otomatik).
  const [detail, setDetail] = useState<any | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin:nav:gr");
      if (!raw) return;
      const params = JSON.parse(raw);
      sessionStorage.removeItem("admin:nav:gr");
      if (typeof params.highlight_id === "string") {
        setHighlightId(params.highlight_id);
      }
      if (typeof params.search === "string") setSearch(params.search);
    } catch { /* ignore */ }
  }, []);

  const {
    items: list,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("goods-receipts", {
    ...(search.trim() ? { search: search.trim() } : {}),
    filters: {
      ...(supplierFilter ? { supplier: supplierFilter } : {}),
      ...statusParams,
    },
  });
  // Tedarikçi filtresi için liste (Select).
  const { data: suppliersData } = useResource<any>("suppliers");
  const suppliers: any[] = suppliersData?.results || suppliersData || [];
  const confirm = useAction("goods-receipts", "confirm");
  const cancel = useAction("goods-receipts", "cancel");
  const [formOpened, formHandlers] = useDisclosure(false);

  // Referansla gelindiyse (highlight_id veya tek sonuçlu arama) ilgili GR
  // listeye düşer düşmez detayını otomatik aç — kullanıcı "ne aldığını"
  // ek tıklama olmadan görsün.
  useEffect(() => {
    if (autoOpened || detail) return;
    if (!highlightId && !search.trim()) return;
    const match = highlightId
      ? list.find((g) => g.id === highlightId)
      : (list.length === 1 ? list[0] : undefined);
    if (match) {
      setDetail(match);
      setAutoOpened(true);
    }
  }, [list, highlightId, search, autoOpened, detail]);

  // Açık detay drawer'ını taze liste verisiyle senkronla. Fatura kesilince
  // (SupplierInvoiceFormModal goods-receipts'i invalidate eder) liste yeniden
  // çekilir; drawer'daki `detail` snapshot'ı is_invoiced/status bakımından
  // güncellenmeli ki "faturalandı" durumu ve buton görünürlüğü anında yansısın.
  useEffect(() => {
    if (!detail) return;
    const fresh = list.find((g) => g.id === detail.id);
    // Sadece anlamlı alanlar değiştiyse güncelle — aksi halde her refetch yeni
    // referans döndürdüğü için sonsuz set-state döngüsüne girerdik.
    if (
      fresh &&
      (fresh.status !== detail.status ||
        fresh.is_invoiced !== detail.is_invoiced ||
        fresh.invoice_id !== detail.invoice_id)
    ) {
      setDetail(fresh);
    }
  }, [list, detail]);

  async function doConfirm(id: string) {
    try {
      await confirm.mutateAsync({ id });
      notifications.show({ message: "GR onaylandı, stok güncellendi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  // Taslak (DRAFT) mal girişini iptal et. DRAFT henüz stok/cari işlemediği için
  // iptal yalnız belgeyi CANCELLED yapar — geri alınacak stok hareketi yoktur.
  // (Onaylı GR iptali stok/maliyeti geri alır; o detay drawer'ından yapılır,
  // burada yanlışlıkla tıklanmaması için yalnız DRAFT'a iptal butonu konur.)
  function askCancel(g: any) {
    modals.openConfirmModal({
      title: `${g.gr_number} taslağını iptal et`,
      children: (
        <Text size="sm">
          Bu taslak mal girişi iptal edilecek. Henüz onaylanmadığı için stok ve
          cari etkilenmez. Devam edilsin mi?
        </Text>
      ),
      labels: { confirm: "Mal Girişini İptal Et", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await cancel.mutateAsync({ id: g.id });
          notifications.show({ message: "Mal girişi iptal edildi", color: "orange" });
        } catch (e: any) {
          notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
        }
      },
    });
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Mal Girişleri (İrsaliye)"
        subtitle={`${total} kayıt`}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={formHandlers.open}>
            Yeni Mal Kabul
          </Button>
        }
      />
      <Group>
        <TextInput
          placeholder="GR no, tedarikçi veya irsaliye no ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Select
          placeholder="Tüm tedarikçiler"
          clearable
          searchable
          value={supplierFilter}
          onChange={setSupplierFilter}
          data={suppliers.map((s) => ({ value: s.id, label: s.name }))}
          style={{ width: 240 }}
        />
        <Select
          placeholder="Tüm durumlar"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            { value: "DRAFT", label: "Onay Bekliyor" },
            { value: "CONFIRMED", label: "Onaylandı" },
            { value: "INVOICED", label: "Faturalandı" },
            { value: "CANCELLED", label: "İptal" },
          ]}
          style={{ width: 180 }}
        />
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(g) => g.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(g) => setDetail(g)}
          rowStyle={(g) =>
            g.id === highlightId
              ? { backgroundColor: "var(--mantine-color-yellow-1)" }
              : undefined
          }
          emptyContent={<EmptyState title="GR yok" />}
          columns={[
            { header: "GR No", render: (g) => <span style={{ fontWeight: 600 }}>{g.gr_number}</span> },
            { header: "Tedarikçi", render: (g) => g.supplier_name },
            { header: "İrsaliye No", render: (g) => g.delivery_note_number || "-" },
            {
              header: "Satın Alma No",
              render: (g) => g.purchase_order_number || <Text c="dimmed" size="sm">—</Text>,
            },
            { header: "Tarih", render: (g) => formatDateTime(g.received_at) },
            {
              header: "Durum",
              render: (g) =>
                // Onaylı + faturalanmış GR → "Faturalandı" (iş tamamlandı).
                g.status === "CONFIRMED" && g.is_invoiced ? (
                  <Badge color="green" variant="filled">Faturalandı</Badge>
                ) : (
                  <Badge color={STATUS_COLOR[g.status]}>{STATUS_LABEL[g.status] || g.status}</Badge>
                ),
            },
            {
              header: "İşlem",
              render: (g) => (
                // Satır tıklaması da detayı açar; göz ikonu ayrıca açık bir
                // ipucu verir. Buton/ikon olayları satıra taşınmasın.
                <Group gap="xs" wrap="nowrap">
                  <Tooltip label="Detayı gör">
                    <ActionIcon
                      variant="subtle"
                      onClick={(e) => { e.stopPropagation(); setDetail(g); }}
                      aria-label="Detayı gör"
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {/* Bağlı PO varsa — sayfaya gitmeden yan drawer'da göster
                      (PurchaseOrderDetailDrawer; id'den taze çeker). */}
                  {g.purchase_order && (
                    <Tooltip label="Satın alma siparişini gör">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={(e) => { e.stopPropagation(); setPoDetail({ id: g.purchase_order }); }}
                        aria-label="Satın alma siparişini gör"
                      >
                        <IconShoppingCart size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {/* Faturalanmışsa — fatura detay modalını aç (ödendi durumu +
                      ekli gerçek fatura indirme orada). */}
                  {g.is_invoiced && g.invoice_id && (
                    <Tooltip label={`Faturayı gör (${g.invoice_number || ""})`}>
                      <ActionIcon
                        variant="subtle"
                        color="teal"
                        onClick={(e) => { e.stopPropagation(); setInvoiceId(g.invoice_id); }}
                        aria-label="Faturayı gör"
                      >
                        <IconFileInvoice size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {g.status === "DRAFT" && (
                    <>
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCheck size={14} />}
                        onClick={(e) => { e.stopPropagation(); doConfirm(g.id); }}
                      >
                        Onayla
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconX size={14} />}
                        onClick={(e) => { e.stopPropagation(); askCancel(g); }}
                      >
                        İptal
                      </Button>
                    </>
                  )}
                </Group>
              ),
            },
          ]}
        />
      </Card>

      <GoodsReceiptDetailDrawer
        gr={detail}
        onClose={() => setDetail(null)}
        onUpdated={(fresh) => setDetail(fresh)}
      />
      <GoodsReceiptFormModal opened={formOpened} onClose={formHandlers.close} />
      {/* Bağlı PO — sayfaya gitmeden yan drawer (PO sayfasındaki bileşenin aynısı). */}
      <PurchaseOrderDetailDrawer po={poDetail} onClose={() => setPoDetail(null)} />
      {/* Bağlı fatura — id'den çekip detay modalında göster. */}
      <InvoiceDetailById invoiceId={invoiceId} onClose={() => setInvoiceId(null)} />
    </Stack>
  );
}

/** Fatura id'sinden objeyi çekip SupplierInvoiceDetailModal'ı açan ince sarmalayıcı.
 *  GR listesindeki "Faturayı gör" ikonu yalnız invoice_id taşır; modal tam obje ister. */
function InvoiceDetailById({ invoiceId, onClose }: { invoiceId: string | null; onClose: () => void }) {
  const { data: invoice } = useResource<any>(
    "supplier-invoices", { ...(invoiceId ? { id: invoiceId } : {}), enabled: !!invoiceId },
  );
  if (!invoiceId) return null;
  return <SupplierInvoiceDetailModal invoice={invoice || null} onClose={onClose} />;
}

// Mal kabulün ne içerdiğini gösteren detay drawer'ı — kalem kalem hammadde/
// ürün, miktar, birim fiyat ve satır toplamı. Stok Operasyonları'ndaki
// "Mal Girişi GR-..." referansından gelindiğinde otomatik açılır.
// CONFIRMED GR'de her satır tek tek düzeltilebilir (yanlış girilen miktar/
// fiyatı, tüm GR'yi iptal etmeden onarmak için).
export function GoodsReceiptDetailDrawer({
  gr, onClose, onUpdated,
}: { gr: any | null; onClose: () => void; onUpdated: (fresh: any) => void }) {
  const lines: any[] = gr?.lines || [];
  // Hangi satır düzeltme modunda (line.id) — null = hiçbiri.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const correctLine = useAction("goods-receipts", "correct_line");
  const grandTotal = lines.reduce(
    (sum, l) =>
      sum + Number(l.quantity_received || 0) * Number(l.unit_price_minor || 0),
    0,
  );
  const canEdit = gr?.status === "CONFIRMED";

  return (
    <Drawer
      opened={!!gr}
      onClose={onClose}
      position="right"
      size="lg"
      padding="lg"
      title={
        <Group gap="sm">
          <Text fw={700} size="lg">{gr?.gr_number}</Text>
          {gr && (
            <Badge color={STATUS_COLOR[gr.status]}>
              {STATUS_LABEL[gr.status] || gr.status}
            </Badge>
          )}
        </Group>
      }
    >
      {gr && (
        <Stack gap="md">
          <Group gap="xl">
            <Box>
              <Text size="xs" c="dimmed">Tedarikçi</Text>
              <Text fw={500}>{gr.supplier_name || "—"}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">İrsaliye No</Text>
              <Text fw={500}>{gr.delivery_note_number || "—"}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Tarih</Text>
              <Text fw={500}>{formatDateTime(gr.received_at)}</Text>
            </Box>
          </Group>

          <Divider label="Kabul Edilen Kalemler" labelPosition="left" />

          {lines.length === 0 ? (
            <EmptyState title="Bu mal kabulünde kalem yok" />
          ) : (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kalem</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Miktar</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Birim Fiyat</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Tutar</Table.Th>
                  {canEdit && <Table.Th style={{ width: 44 }} />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((l) => {
                  const name = l.product_name || l.ingredient_name || l.supply_item_name || "(adsız)";
                  const lineTotal =
                    Number(l.quantity_received || 0)
                    * Number(l.unit_price_minor || 0);
                  if (canEdit && editingId === l.id) {
                    return (
                      <CorrectionRow
                        key={l.id}
                        line={l}
                        loading={correctLine.isPending}
                        onCancel={() => setEditingId(null)}
                        onSubmit={async (quantity_received, unit_price_minor) => {
                          try {
                            const fresh = await correctLine.mutateAsync({
                              id: gr.id, line_id: l.id,
                              quantity_received, unit_price_minor,
                            });
                            notifications.show({
                              message: "Satır düzeltildi, maliyet güncellendi",
                              color: "green",
                            });
                            setEditingId(null);
                            onUpdated(fresh);
                          } catch (e: any) {
                            notifications.show({
                              message: e?.response?.data?.detail || "Düzeltilemedi",
                              color: "red",
                            });
                          }
                        }}
                      />
                    );
                  }
                  return (
                    <Table.Tr key={l.id}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">{name}</Text>
                          <Badge
                            size="xs"
                            variant="light"
                            color={
                              l.line_type === "PRODUCT" ? "violet"
                              : l.line_type === "SUPPLY_ITEM" ? "teal"
                              : "blue"
                            }
                          >
                            {l.line_type === "PRODUCT" ? "Ürün"
                              : l.line_type === "SUPPLY_ITEM" ? "Mutfak-dışı"
                              : "Hammadde"}
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {formatQty(l.quantity_received)}
                        {l.unit ? ` ${l.unit}` : ""}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {formatTRY(l.unit_price_minor)}
                        {l.unit ? ` / ${l.unit}` : ""}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatTRY(lineTotal)}
                      </Table.Td>
                      {canEdit && (
                        <Table.Td>
                          <Tooltip label="Satırı düzelt">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => setEditingId(l.id)}
                              aria-label="Satırı düzelt"
                            >
                              <IconPencil size={15} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
              <Table.Tfoot>
                <Table.Tr>
                  <Table.Th colSpan={3} style={{ textAlign: "right" }}>
                    Toplam
                  </Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    {formatTRY(grandTotal)}
                  </Table.Th>
                  {canEdit && <Table.Th />}
                </Table.Tr>
              </Table.Tfoot>
            </Table>
          )}
          {canEdit && (
            <Text size="xs" c="dimmed">
              Bir satırı düzeltmek, o kalemin stok hareketini geri alıp yeni
              değerle yeniden işler ve hammadde maliyetini günceller. Diğer
              kalemler etkilenmez.
            </Text>
          )}

          {/* CONFIRMED GR'den fatura üret — satırlar prefilled, goods_receipt_line
              bağlı → fatura işlenince İrsaliye borcu mahsuplaşır (çift kayıt yok).
              Zaten faturalanmış GR'de buton GİZLENİR (çift fatura önlenir). */}
          {gr.status === "CONFIRMED" && !gr.is_invoiced && (
            <Button
              variant="light"
              leftSection={<IconFileInvoice size={16} />}
              onClick={() => setInvoiceOpen(true)}
            >
              Bu Mal Girişinden Fatura Oluştur
            </Button>
          )}
          {gr.status === "CONFIRMED" && gr.is_invoiced && (
            <Text size="sm" c="dimmed" ta="center">
              Bu mal girişi faturalandı — tekrar fatura kesilemez.
            </Text>
          )}
        </Stack>
      )}

      <SupplierInvoiceFormModal
        opened={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        prefill={
          gr
            ? {
                supplierId: gr.supplier,
                supplierName: gr.supplier_name || "",
                goodsReceiptId: gr.id,
                lines: lines.map((l): InvoicePrefillLine => ({
                  grLineId: l.id,
                  itemName: l.ingredient_name || l.product_name || l.supply_item_name || "(kalem)",
                  quantity: l.quantity_received,
                  unitPriceMinor: l.unit_price_minor,
                })),
              }
            : null
        }
      />
    </Drawer>
  );
}

// Bir GR satırının inline düzeltme formu (CONFIRMED GR). Kullanıcı miktar ve
// fiyatı ALIŞ birimiyle (kg/lt) girer; mevcut stok-birimi değerleri alış
// birimine geri çevrilerek ön-doldurulur. Submit'te lineToPayload ile yeniden
// stok birimine çevrilip backend'e gönderilir (Yeni Mal Kabul formuyla aynı
// dönüşüm — bkz. goodsReceiptUnits.ts).
function CorrectionRow({
  line, loading, onSubmit, onCancel,
}: {
  line: any;
  loading: boolean;
  onSubmit: (quantityReceived: number, unitPriceMinor: number) => void;
  onCancel: () => void;
}) {
  const stockUnit: string = line.unit || "adet";
  const options = purchaseUnitsFor(stockUnit);
  // Varsayılan alış birimi: büyük birim (kg/lt) — kullanıcı faturada onu görür.
  const [purchaseUnit, setPurchaseUnit] = useState<string>(
    options[options.length - 1].value,
  );
  const factor = factorFor(stockUnit, purchaseUnit);
  // Ön-doldurma: stok birimi değerlerini alış birimine GERİ çevir.
  const [quantity, setQuantity] = useState<number | string>(
    Number(line.quantity_received) / factor,
  );
  const [priceMinor, setPriceMinor] = useState<number | string>(
    Math.round(Number(line.unit_price_minor) * factor),
  );

  // Alış birimi değişince miktar/fiyatı yeni faktöre göre yeniden ölçekle —
  // gösterilen değer hep aynı fiziksel miktarı/fiyatı temsil etsin.
  function changeUnit(next: string) {
    const newFactor = factorFor(stockUnit, next);
    setQuantity(Number(line.quantity_received) / newFactor);
    setPriceMinor(Math.round(Number(line.unit_price_minor) * newFactor));
    setPurchaseUnit(next);
  }

  function submit() {
    const { quantityReceived, unitPriceMinor } = lineToPayload({
      stockUnit, purchaseUnit,
      quantity: Number(quantity),
      purchasePriceMinor: Number(priceMinor),
    });
    onSubmit(quantityReceived, unitPriceMinor);
  }

  const name = line.product_name || line.ingredient_name || line.supply_item_name || "(adsız)";

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" fw={500}>{name}</Text>
        <Group gap={4} mt={4} wrap="nowrap">
          {options.length > 1 ? (
            <Select
              value={purchaseUnit}
              onChange={(v) => v && changeUnit(v)}
              data={options.map((o) => ({ value: o.value, label: o.label }))}
              size="xs"
              w={80}
              allowDeselect={false}
            />
          ) : (
            <Text size="xs" c="dimmed">{purchaseUnit}</Text>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <NumberInput
          value={quantity}
          onChange={setQuantity}
          min={0}
          step={0.1}
          decimalScale={3}
          size="xs"
          suffix={` ${purchaseUnit}`}
        />
      </Table.Td>
      <Table.Td>
        <NumberInput
          value={Number(priceMinor) / 100}
          onChange={(v) => setPriceMinor(Math.round(Number(v) * 100))}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          suffix={` ₺/${purchaseUnit}`}
          size="xs"
        />
      </Table.Td>
      <Table.Td colSpan={2}>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <Button size="xs" onClick={submit} loading={loading}>Kaydet</Button>
          <Button size="xs" variant="default" onClick={onCancel} disabled={loading}>
            İptal
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
